import { ReactElement, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '../controls/Button';
import SearchInput from '../controls/SearchInput';
import Filter from '../controls/Filter';
import SidePanelContainer from '../controls/SidePanelContainer';
import GatewaysTable from './GatewaysTable';
import AutoDetection from './AutoDetection';
import { GatewayDetails, ActionData } from '../constants/telosair-interfaces';
import {
  getAllGatewaylist,
  deleteGateway,
  createGateway,
  editGateway
} from '../services/dashboard-service';
import OverlayDialog from '../controls/OverlayDialog';
import { TOAST_MESSAGES } from '../constants/global-constants';
import CustomInputBox from '../controls/CustomInputBox';

type FORMDATA = {
  gatewayId: string;
  gatewayName: string;
};

/**
 * Analytics Component renders Analytics page.
 * @returns - Analytics Component.
 */
export default function Gateways(): ReactElement {
  const [isManual, setIsManual] = useState(false);
  const [gatewayData, setGatewayData] = useState<GatewayDetails[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [dropdowntext, setDropdownText] = useState('');
  const [uniqueid, setUniqueId] = useState(0);
  const methods = useForm<FORMDATA>({
    mode: 'all'
  });
  const { reset, handleSubmit } = methods;
  /**
   * Close the modal.
   */
  const handleClose = (): void => {
    setIsModalOpen(false);
    setDropdownText('');
    reset({ gatewayId: '', gatewayName: '' });
  };

  /**
   * Calls All Gatewayslist.
   */
  const fetchAllGatewaylist = async (): Promise<void> => {
    try {
      const response = await getAllGatewaylist();
      setGatewayData(response.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };
  useEffect(() => {
    fetchAllGatewaylist();
  }, []);

  /**
   * Handle create the gateway.
   * @param data - Formdata.
   */
  const handleCreateGateway = async (data: FORMDATA): Promise<void> => {
    const payload = {
      gatewayName: data.gatewayName,
      gatewayId: data.gatewayId,
      facilityId: 1
    };
    try {
      await createGateway(payload);
      fetchAllGatewaylist();
      reset({ gatewayId: '', gatewayName: '' });
      toast.success(TOAST_MESSAGES.created);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error(TOAST_MESSAGES.error);
    }
  };
  /**
   * Handle Edit Gateway.
   * @param data - Formdata.
   */
  const handleEditGateway = async (data: FORMDATA): Promise<void> => {
    const payload = {
      id: uniqueid,
      gatewayName: data.gatewayName,
      gatewayId: data.gatewayId,
      facilityId: 1
    };
    try {
      await editGateway(uniqueid, payload);
      fetchAllGatewaylist();
      reset({ gatewayId: '', gatewayName: '' });
      toast.success(TOAST_MESSAGES.edited);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error(TOAST_MESSAGES.error);
    }
  };
  /**
   * Delete Function of the Gateway.
   * @param id - Gatewayuniqueid to delete.
   */
  const handledeleteGateway = async (id: number): Promise<void> => {
    try {
      await deleteGateway(id);
      fetchAllGatewaylist();
      toast.success(TOAST_MESSAGES.deleted);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error(TOAST_MESSAGES.error);
    }
  };

  /**
   * Handling the delte and edit Gateway function.
   * @param action - Calling te above function.
   */
  const handleAction = async (action: ActionData<GatewayDetails>): Promise<void> => {
    if (action.type === 'delete') {
      handledeleteGateway(action.data.id);
    }
    if (action.type === 'edit') {
      setIsModalOpen(true);
      reset({ gatewayId: action.data.gatewayId, gatewayName: action.data.gatewayName });
      setUniqueId(action.data.id);
      setDropdownText('edit');
    }
  };
  /**
   * Handles the Submit.
   * @param data - Formdata.
   */
  const onhandleSubmit = (data: FORMDATA): void => {
    if (dropdowntext === 'edit') {
      handleEditGateway(data);
      handleClose();
    } else {
      handleCreateGateway(data);
      handleClose();
      setOpenConfirmation(true);
    }
  };
  return (
    <>
      <div className="flex flex-col sm:flex-row gap-4 justify-between rounded bg-white dark:bg-container-500 p-4 mt-4">
        <div className="flex items-center space-x-4 invisible">
          <SearchInput textValue={searchText} setText={setSearchText} />
          <Filter position="left" />
        </div>
        <div className="mr-4">
          <Button
            theme="primary"
            size="sm"
            name="Add Gateway"
            onClick={() => {
              setIsModalOpen(true);
            }}
            isBlockButton
          />
        </div>
      </div>
      <div className="mt-4">
        <GatewaysTable gateways={gatewayData} handleGatewayAction={handleAction} />
      </div>
      {isModalOpen && (
        <SidePanelContainer closeModal={handleClose}>
          {!isManual ? (
            <>
              <div className="text-gray-300 dark:text-gray-300 text-base">Gateway detection</div>
              <div className="pb-3 flex text-gray-500 dark:text-gray-400 text-sm">
                Experience hassle-free connectivity with TelosAir&apos;s automatic gateway detection
                feature.
              </div>
              {
                // TODO: insert devices array here
              }
              {isModalOpen && (
                <AutoDetection
                  devices={[]}
                  onAddManual={() => setIsManual((prevState) => !prevState)}
                  onSave={() => {
                    handleClose();
                    setOpenConfirmation(true);
                  }}
                />
              )}
            </>
          ) : (
            <>
              <div className="text-gray-300 dark:text-gray-300 text-base flex">
                <button
                  type="button"
                  className="material-icons-outlined text-md cursor-pointer"
                  onClick={() => setIsManual((prevState) => !prevState)}
                >
                  chevron_left
                </button>
                Add manually
              </div>
              {/* eslint-disable-next-line */}
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onhandleSubmit)}>
                  <div className="pb-3 flex text-gray-500 dark:text-gray-400 text-sm">
                    Use this feature to add gateways manually when they are not detected
                    automatically.
                  </div>
                  <CustomInputBox
                    idname="gatewayId"
                    validationObject={{ required: 'Please enter the gateway ID' }}
                    label="Gateway ID"
                  />
                  <CustomInputBox
                    idname="gatewayName"
                    label="Gateway Name"
                    validationObject={{ required: 'Please enter the gateway name' }}
                  />
                  <div className="mt-6 pt-6 border-t border-t-gray-200 dark:border-t-gray-700">
                    <Button
                      theme="primary"
                      size="sm"
                      name="Save Changes"
                      buttonType="submit"
                      isBlockButton
                    />
                  </div>
                </form>
              </FormProvider>
            </>
          )}
        </SidePanelContainer>
      )}
      <div className="max-w-[314px]">
        <OverlayDialog open={openConfirmation} setOpen={() => setOpenConfirmation(false)} />
      </div>
    </>
  );
}
